import { useState } from 'react';
import { Plus, X, Info, Check, PackageCheck, Truck } from 'lucide-react';
import { clsx } from 'clsx';
import { useBeschaffungswege } from '@/hooks/useBeschaffungswege';
import { datumDe, datumKurzDe, heuteIso } from '@/lib/format';
import { WEG_VERLAG, KANAELE, KANAL_SONSTIGES } from '@/lib/beschaffungWege';
import { SchnellwegKnoepfe } from '@/components/ui/SchnellwegKnoepfe';
import { sendungsstandAnzeige } from '@/lib/sendungsstand';
import { UmhaengenFrage, type UmhaengenZiel } from './UmhaengenFrage';
import type { DetailPosition } from '@/types/detail';
import { FARBE_NUTZER_FALLBACK } from '@/theme';

// Der Beschaffungs-Teil einer Positionszeile.
//
// Beantwortet die Frage, die vorher niemand aus dem System beantworten
// konnte: von den fünf Stück — wo sind welche bestellt? Fünf Exemplare
// laufen im Laden regelmäßig über zwei oder drei Wege, weil kein einzelner
// sie liefert.
//
// Die Summe wird NICHT hier gerechnet, sie kommt vom Server
// (`beschaffung_summe`). Wer sie in der Oberfläche noch einmal ausrechnet,
// baut die zweite Wahrheit, die das Journal gerade vermeiden soll — und
// bekommt sie irgendwann anders heraus als das Dashboard daneben.
//
// „Zugeordnet 3/5" nennt die WIRKSAME Menge: was eingetroffen ist, wurde
// zwangsläufig beschafft, auch wenn im Journal nichts dazu steht. Die
// Zahl kann deshalb über der Summe der Chips liegen — dann sagt die
// Sprechblase, woher der Unterschied kommt. Zufügen bleibt trotzdem
// möglich, damit das Journal nachtragbar ist.
//
// DAS WORT HIESS BIS A12.1 „BESTELLT" (E5)
//   Es stimmte nicht. Zugeordnet ist die Menge einem Beschaffungsweg;
//   bestellt ist sie damit nicht zwingend — der Weg „Auf Lager" ist gar
//   keine Bestellung, und ein Buch aus dem eigenen Regal wurde nie
//   bestellt. Wer „Bestellt 1/1" las und der Kundin sagte „ist
//   unterwegs", sagte etwas, das niemand behauptet hatte.
//
//   Die Zahlen und ihre Rechnung sind unverändert. Nur das Wort ist
//   jetzt wahr.
//
// Beim Weg „Verlag" kommen zwei Angaben dazu: bei WELCHEM Verlag und
// über welchen Kanal. „Verlag" allein ist keine Auskunft — wer morgen
// nachhakt, muss wissen, wo er anruft (Migration 018).
//
// Seit Migration 020 trägt jeder Eintrag zusätzlich, wieviel ÜBER IHN
// eingetroffen ist. Der Chip sagt das mit („2× Libri · 1 da"), und was
// keinem Weg zugeordnet ist, bekommt einen eigenen Chip. Beides ist
// nötig, damit die Frage beantwortbar bleibt, die dieses Paket stellt:
// von den fünf Stück — welche sind schon da, und von wem?
//
// Die Lieferantenmeldung (E3) steht als eigene Zeile unter den Chips,
// nicht nur in einer Sprechblase: Sprechblasen gibt es auf einem Tablet
// nicht (CLAUDE.md — keine Hover-only-Zustände). Ausgelegt wird sie
// nirgends. „Nachdruck KW 40" ist ein Satz für Menschen; wer daraus ein
// Datum errechnet, erfindet eine Zusage, die der Lieferant nie gemacht
// hat.

type Props = {
  position: DetailPosition;
  onAdd: (eintrag: BeschaffungEingabe) => void;
  onDelete: (id: number, beschriftung: string) => void;
  /**
   * Einen unzugeordneten Eingang einem Weg zuschlagen (A60).
   *
   * Die Sektion entscheidet nur, OB gefragt wird, und stellt die
   * Auswahl zusammen; gerufen wird der Endpunkt von der Positionsliste,
   * wie bei allen Nachbarn hier.
   */
  onUmhaengen: (beschaffungId: number, menge: number) => void;
  /** Meldung am BESTEHENDEN Eintrag ändern oder entfernen (E11b). */
  onMeldungSave: (
    beschaffungId: number,
    meldung: string | null,
    meldungAm: string | null
  ) => void;
};

/** Was das Formular meldet. Verlag und Kanal nur beim Weg „Verlag". */
export type BeschaffungEingabe = {
  wegId: number;
  menge: number;
  datum: string;
  verlagText: string | null;
  kanal: string | null;
  /** Was der Lieferant zu diesem Vorgang gemeldet hat (E3). */
  meldung: string | null;
  /** Und wann (E15). Ohne Meldung null. */
  meldungAm: string | null;
};

/**
 * Wie ein Eintrag heißt — am Chip, in der Rückfrage, im Verlauf.
 *
 * Beim Verlag steht sein Name statt des Wegs: „2× Urachhaus" sagt mehr
 * als „2× Verlag". Ohne Namen bleibt es beim Weg, so wie bisher und wie
 * bei allen Bestandseinträgen.
 */
export function beschaffungBeschriftung(b: {
  menge: number;
  verlag_text: string | null;
  weg: { label: string };
}): string {
  return `${b.menge}× ${b.verlag_text ?? b.weg.label}`;
}

export function BeschaffungSektion({
  position: p,
  onAdd,
  onDelete,
  onUmhaengen,
  onMeldungSave
}: Props) {
  const [formOffen, setFormOffen] = useState(false);
  /**
   * Welcher Eintrag gerade seine Meldung bearbeiten lässt (E11b).
   *
   * Bis hierher ging das nur über Löschen und Neuanlegen des ganzen
   * Journaleintrags. Das ist der falsche Preis: der Eintrag hält fest,
   * WANN bei WEM WIEVIEL bestellt wurde und wer das erfasst hat. Wer ihn
   * wegwirft, um einen Satz zu korrigieren, wirft diese Tatsachen mit
   * weg. Die Meldung ist eine Auskunft ÜBER den Vorgang, keine Tatsache
   * DES Vorgangs — sie darf sich ändern.
   */
  const [meldungFuer, setMeldungFuer] = useState<number | null>(null);
  const [meldungText, setMeldungText] = useState('');
  const [meldungAmText, setMeldungAmText] = useState(heuteIso());
  const [nachtragOffen, setNachtragOffen] = useState(false);
  const [umhaengenOffen, setUmhaengenOffen] = useState(false);
  const wege = useBeschaffungswege();

  /**
   * A60 — wohin ein unzugeordneter Eingang überhaupt könnte.
   *
   * Nur Einträge mit Platz: über einen Weg kann nicht mehr eingetroffen
   * sein, als über ihn bestellt wurde. Dieselbe Grenze zieht der Server
   * (`exceeds_weg`) — hier steht sie, damit erst gar kein Knopf
   * erscheint, der sicher abgewiesen würde.
   */
  const umhaengeZiele: UmhaengenZiel[] = p.beschaffungen
    .filter((b) => b.menge > b.menge_eingegangen)
    .map((b) => ({
      beschaffung_id: b.id,
      label: beschaffungBeschriftung(b),
      offen: b.menge - b.menge_eingegangen
    }));

  const mitMeldung = p.beschaffungen.filter((b) => b.meldung !== null && b.meldung !== '');
  const ohneMeldung = p.beschaffungen.filter((b) => b.meldung === null || b.meldung === '');

  function meldungBearbeiten(id: number, wert: string | null, am: string | null): void {
    setMeldungText(wert ?? '');
    // Vorhandenes Datum übernehmen, sonst heute — der Lieferschein von
    // vorgestern bleibt überschreibbar.
    setMeldungAmText(am ?? heuteIso());
    setMeldungFuer(id);
  }

  function meldungSpeichern(id: number): void {
    const t = meldungText.trim();
    // Leer heißt löschen — der Normalweg für "hat sich erledigt". Das
    // Datum geht dann mit, hier wie im Server.
    onMeldungSave(id, t === '' ? null : t, t === '' ? null : meldungAmText);
    setMeldungFuer(null);
  }

  return (
    <div className="mt-1 space-y-1.5 border-t border-line pt-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className="text-ink-soft"
          title={
            p.beschaffung_summe > p.beschaffung_journal_summe
              ? `${p.beschaffung_journal_summe} laut Journal, ${p.menge_eingegangen} bereits eingetroffen — Eingetroffenes gilt als zugeordnet.`
              : undefined
          }
        >
          Zugeordnet{' '}
          <span className="tabular-nums text-ink">
            {/* Gegen die wirksame Menge: Vergriffenes wird nicht
                zugeordnet (Migration 035). */}
            {p.beschaffung_summe}/{p.menge_wirksam}
          </span>
        </span>
        {p.beschaffung_offen > 0 && (
          <span className="rounded bg-gold-soft px-1.5 py-0.5 tabular-nums text-gold-dark">
            {p.beschaffung_offen} offen
          </span>
        )}

        {/* A12.4 — WO STECKT DIESE POSITION?
            Die Beschaffung sagt, woher das Buch kommt. Das hier sagt,
            wohin es gegangen ist. Beides gehoert in dieselbe Zeile: wer
            eine Bestellung mit vier Titeln aufmacht, will an EINER Stelle
            je Zeile sehen, wie weit sie ist.

            Zwei Farben fuer drei Zustaende: „bereitgelegt" ist Gold wie
            alles, was noch Arbeit ist; „draussen" ist Teal wie alles
            Erledigte. Der dritte Zustand — gar keiner Sendung zugeordnet
            — zeigt nichts, sonst stuende an jeder frischen Position ein
            Merkzeichen, das nur „nein" sagt. */}
        <Sendungsstand position={p} />

        {/* Was keinem Weg zugeordnet ist. Kein Löschknopf: hier steht
            kein Vorgang, sondern eine Restgröße — VERRINGERT wird sie
            über den Eingangs-Zähler, nicht hier.

            SEIT A60 IST DER CHIP EIN KNOPF, und das ist kein Widerspruch
            zu dem Satz darüber: Umhängen löscht nichts, es verschiebt.
            Die eingetroffene Gesamtmenge bleibt gleich, sie steht danach
            nur an einem anderen Ort. Genau hier steht die falsche
            Auskunft („1× Libri, nichts eingetroffen" neben „1× nicht
            zugeordnet"), und genau hier steht die Zahl, um die es geht —
            deshalb hängt der Vorgang an dieser Stelle und nicht am
            Eingangs-Zähler, der nur Gesamtmengen kennt.

            OHNE ZIEL BLEIBT ES ANZEIGE. Hat die Position keinen Eintrag
            mit Platz, wird der Chip NICHT zum Knopf: Ein Knopf, der
            einen leeren Dialog öffnet, ist schlechter als kein Knopf.

            `touch-ziel` wie beim X-Knopf im Nachbar-Chip: Die Chipzeile
            ist niedriger als 44 Pixel, das Trefferfeld darf es nicht
            sein. */}
        {p.menge_eingegangen_ohne_weg > 0 &&
          (umhaengeZiele.length > 0 ? (
            <button
              type="button"
              onClick={() => setUmhaengenOffen(true)}
              aria-label={`${p.menge_eingegangen_ohne_weg}× nicht zugeordnet — einem Weg zuschlagen`}
              title="Eingetroffen, ohne dass ein Beschaffungsweg dazu bekannt ist. Antippen, um es einem Weg zuzuschlagen."
              className="touch-ziel inline-flex items-center gap-1 rounded-full border border-dashed border-line bg-cream-warm px-2 py-0.5 text-ink-faint hover:border-akzent hover:text-akzent"
            >
              <span className="tabular-nums">{p.menge_eingegangen_ohne_weg}×</span>
              nicht zugeordnet
            </button>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-line bg-cream-warm px-2 py-0.5 text-ink-faint"
              title="Eingetroffen, ohne dass ein Beschaffungsweg dazu bekannt ist."
            >
              <span className="tabular-nums">{p.menge_eingegangen_ohne_weg}×</span>
              nicht zugeordnet
            </span>
          ))}

        {p.beschaffungen.map((b) => (
          <span
            key={b.id}
            className="inline-flex items-center gap-1 rounded-full border border-line bg-cream-warm py-0.5 pl-2 pr-0.5 text-ink-soft"
            title={[
              // Der Weg gehört in die Sprechblase, sobald der Chip den
              // Verlagsnamen trägt — sonst stünde nirgends mehr, dass es
              // eine Verlagsbestellung war.
              b.verlag_text !== null ? b.weg.label : null,
              b.kanal_label,
              b.meldung,
              datumDe(b.bestellt_am),
              `${b.erfasser.kuerzel} · ${b.erfasser.name}`
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            {/* Ein Punkt, keine Einfärbung: hier steht ein FREMDER
                Urheber. Die Farbe des Angemeldeten trägt seit N6 die
                Akzentfarbe der ganzen Oberfläche (hooks/useAkzentFarbe.ts)
                — würde sie hier auch als Fläche auftreten, hieße dieselbe
                Farbe an zwei Stellen Verschiedenes: einmal „das bin ich",
                einmal „das war jemand anders". Der Punkt hält die beiden
                Aussagen auseinander, so wie im Erfasser-Merkzeichen des
                Dashboards und im Verlauf. */}
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: b.erfasser.farbe ?? FARBE_NUTZER_FALLBACK }}
            />
            <span className="tabular-nums">{beschaffungBeschriftung(b)}</span>
            {b.menge_eingegangen > 0 && (
              <span className="tabular-nums text-teal">· {b.menge_eingegangen} da</span>
            )}
            {b.meldung !== null && b.meldung !== '' && (
              <Info aria-hidden className="h-3 w-3 shrink-0 text-gold-dark" />
            )}
            {/* Eigener Knopf statt eines Klicks auf den ganzen Chip: der
                Chip trägt eine Sprechblase mit Datum und Kürzel, und ein
                Tippen darauf soll die nicht löschen. */}
            <button
              type="button"
              onClick={() => onDelete(b.id, beschaffungBeschriftung(b))}
              aria-label={`Beschaffung ${beschaffungBeschriftung(b)} entfernen`}
              className="touch-ziel inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-faint hover:bg-coral-soft hover:text-coral"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        {/* Maßgeblich ist hier das JOURNAL, nicht die wirksame Menge: bei
            einer eingetroffenen Position ohne Eintrag ist nichts mehr
            „offen", nachtragen soll man trotzdem können. Erst wenn das
            Journal voll ist, verschwindet der Knopf — dann würde das
            Formular eine Menge vorschlagen, die der Server ablehnt. */}
        {p.beschaffung_journal_offen > 0 && !formOffen && (
          <button
 data-nutzung="weg_zuordnen"            type="button"
            onClick={() => setFormOffen(true)}
            className="ml-auto inline-flex min-h-[44px] items-center gap-1 rounded-md px-2 text-ink-soft hover:text-akzent"
          >
            <Plus className="h-3.5 w-3.5" />
            Weg zuordnen
          </button>
        )}
      </div>

      {/* DIE SCHNELLWEGE (A12.2, seit A19.1 ein gemeinsames Bauteil)

          Drei Knöpfe für die drei häufigsten Fälle. Jeder legt die GANZE
          offene Menge in einem Griff auf seinen Weg — genau das, was man
          sonst über das Formular tut: Weg wählen, Menge steht schon,
          „Zufügen".

          Sie ersetzen das Formular nicht. Wer eine Teilmenge aufteilt,
          einen Verlag benennt oder ein anderes Datum braucht, geht
          weiterhin dorthin. Der Schnellweg nimmt nur den Normalfall ab.

          Dasselbe Bauteil steht seit A19 auch im Erfassungsformular. Die
          Auswahl, ihre Reihenfolge und das Verschwinden eines fehlenden
          Wegs stehen deshalb nur noch an einer Stelle. */}
      {!formOffen && (
        <SchnellwegKnoepfe
          offen={p.beschaffung_journal_offen}
          tourAnker
          onWeg={(w, menge) =>
            onAdd({
              wegId: w.id,
              menge,
              datum: heuteIso(),
              // Auch beim Schnellweg „Verlag" bleiben beide leer (D3,
              // Entscheidung des Betreibers): Kanal und Verlagsname
              // sind freiwillig, und der Schnellweg fragt nichts.
              // Wer sie festhalten will, nimmt das Formular.
              verlagText: null,
              kanal: null,
              meldung: null,
              meldungAm: null
            })
          }
        />
      )}

      {/* Was die Lieferanten gemeldet haben. Eine Zeile je Eintrag, in
          derselben Reihenfolge wie die Chips darüber — und antippbar,
          weil sich eine Meldung ändert, sobald der Lieferant etwas Neues
          sagt. */}
      {mitMeldung.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {mitMeldung.map((b) =>
            meldungFuer === b.id ? (
              <li key={b.id}>
                <MeldungFeld
                  wert={meldungText}
                  onWert={setMeldungText}
                  datum={meldungAmText}
                  onDatum={setMeldungAmText}
                  onSpeichern={() => meldungSpeichern(b.id)}
                  onAbbrechen={() => setMeldungFuer(null)}
                />
              </li>
            ) : (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => meldungBearbeiten(b.id, b.meldung, b.meldung_am)}
                  className="flex w-full min-h-[44px] items-center gap-1.5 rounded px-1 text-left italic text-gold-dark hover:bg-gold-soft"
                >
                  <Info aria-hidden className="h-3 w-3 shrink-0" />
                  {/* Eine Zeile, die nicht umbricht und nicht herausläuft
                      (E15): Weg und Datum stehen fest, der Meldungstext
                      wird abgeschnitten. Vollständig steht er im Feld,
                      das dieser Knopf öffnet — und in der Sprechblase
                      des Merkzeichens in der Kopfzeile. */}
                  <span className="shrink-0 not-italic text-ink-faint">
                    {b.verlag_text ?? b.weg.label}
                    {b.meldung_am !== null && `, ${datumKurzDe(b.meldung_am)}`}:
                  </span>
                  <span className="min-w-0 truncate">{b.meldung}</span>
                </button>
              </li>
            )
          )}
        </ul>
      )}

      {/* Meldung an einem Eintrag NACHTRAGEN, der noch keine hat.
          Genau ein Kandidat: ohne Rückfrage. Mehrere: die Wahl, in
          derselben knappen Form wie beim Wareneingang (E2). */}
      {ohneMeldung.length > 0 && meldungFuer === null && !nachtragOffen && (
        <button
 data-nutzung="meldung_erfassen"          type="button"
          onClick={() => {
            if (ohneMeldung.length === 1) meldungBearbeiten(ohneMeldung[0].id, null, null);
            else setNachtragOffen(true);
          }}
          className="inline-flex min-h-[44px] items-center gap-1 rounded-md px-1 text-xs text-ink-faint hover:text-akzent"
        >
          <Plus className="h-3 w-3" />
          Meldung erfassen
        </button>
      )}
      {nachtragOffen && meldungFuer === null && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-ink-faint">Zu welchem Weg?</span>
          {ohneMeldung.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                setNachtragOffen(false);
                meldungBearbeiten(b.id, null, null);
              }}
              className="inline-flex min-h-[44px] items-center rounded-md border border-line px-2 text-ink-soft hover:border-akzent hover:text-akzent"
            >
              {beschaffungBeschriftung(b)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setNachtragOffen(false)}
            className="min-h-[44px] px-2 text-ink-faint hover:text-coral"
          >
            Abbrechen
          </button>
        </div>
      )}
      {meldungFuer !== null && !mitMeldung.some((b) => b.id === meldungFuer) && (
        <MeldungFeld
          wert={meldungText}
          onWert={setMeldungText}
          datum={meldungAmText}
          onDatum={setMeldungAmText}
          onSpeichern={() => meldungSpeichern(meldungFuer)}
          onAbbrechen={() => setMeldungFuer(null)}
        />
      )}

      {formOffen && (
        <BeschaffungFormular
          restmenge={p.beschaffung_journal_offen}
          wege={wege.data?.items ?? []}
          verlagVorschlag={p.verlag_vorschlag}
          onAbbrechen={() => setFormOffen(false)}
          onZufuegen={(eingabe) => {
            onAdd(eingabe);
            setFormOffen(false);
          }}
        />
      )}

      {/* A60: „Wohin gehört es?" — nur montiert, solange sie offen ist.
          Der Dialog merkt sich eine getippte Menge; bliebe er im Baum
          stehen, trüge er sie beim nächsten Öffnen noch. */}
      {umhaengenOffen && (
        <UmhaengenFrage
          offen
          titel={p.titel}
          ohneWeg={p.menge_eingegangen_ohne_weg}
          ziele={umhaengeZiele}
          onWahl={(bid, menge) => {
            setUmhaengenOffen(false);
            onUmhaengen(bid, menge);
          }}
          onAbbruch={() => setUmhaengenOffen(false)}
        />
      )}
    </div>
  );
}

type Weg = { id: number; code: string; label: string };

function BeschaffungFormular({
  restmenge,
  wege,
  verlagVorschlag,
  onZufuegen,
  onAbbrechen
}: {
  restmenge: number;
  wege: Weg[];
  verlagVorschlag: string | null;
  onZufuegen: (eingabe: BeschaffungEingabe) => void;
  onAbbrechen: () => void;
}) {
  // Vorbelegt mit dem, was in neun von zehn Fällen stimmt: die volle
  // Restmenge, heute, der oberste Weg der Liste. Wer nur „Zufügen“ drückt,
  // hat den häufigsten Fall erfasst.
  const [wegId, setWegId] = useState<number | ''>('');
  const [menge, setMenge] = useState(restmenge);
  const [datum, setDatum] = useState(heuteIso());
  // Der Verlag der Position als Vorschlag — überschreibbar, denn bestellt
  // wird auch mal woanders als beim Hersteller des Buches.
  const [verlagText, setVerlagText] = useState(verlagVorschlag ?? '');
  // Leer statt vorbelegt: ein Kanal, den niemand gewählt hat, wäre eine
  // Behauptung über einen Vorgang (Designprinzip 3 — protokollieren,
  // nicht erfinden).
  const [kanalWahl, setKanalWahl] = useState('');
  const [kanalFrei, setKanalFrei] = useState('');
  // Freitext, kein Datumsfeld und keine Auswahl. Was auf dem Lieferschein
  // steht, passt in kein Schema: mal eine Kalenderwoche, mal eine
  // Rückstandsnummer, mal ein ganzer Satz.
  const [meldung, setMeldung] = useState('');
  // Heute vorbelegt und überschreibbar: der Lieferschein kann von
  // vorgestern sein. Erzwungen wird das Datum nicht.
  const [meldungAm, setMeldungAm] = useState(heuteIso());

  const gewaehlt = wegId === '' ? (wege[0]?.id ?? null) : wegId;
  const istVerlag = wege.find((w) => w.id === gewaehlt)?.code === WEG_VERLAG;
  const zeigeFreitext = kanalWahl === KANAL_SONSTIGES;
  const gueltig =
    gewaehlt !== null &&
    menge >= 1 &&
    menge <= restmenge &&
    datum !== '' &&
    // Wer „Sonstiges" wählt, muss auch sagen was — sonst stünde am Ende
    // ein leerer Kanal, und die Auswahl hätte nichts festgehalten.
    (!istVerlag || !zeigeFreitext || kanalFrei.trim() !== '');

  function melden(): void {
    if (gewaehlt === null) return;
    const kanal = zeigeFreitext ? kanalFrei.trim() : kanalWahl;
    onZufuegen({
      wegId: gewaehlt,
      menge,
      datum,
      // Bei anderen Wegen gar nicht erst mitschicken. Der Server verwirft
      // sie ohnehin — aber was nicht gesendet wird, kann auch nicht
      // versehentlich ankommen.
      verlagText: istVerlag && verlagText.trim() !== '' ? verlagText.trim() : null,
      kanal: istVerlag && kanal !== '' ? kanal : null,
      meldung: meldung.trim() !== '' ? meldung.trim() : null,
      // Ohne Meldung kein Datum — dieselbe Regel wie im Server.
      meldungAm: meldung.trim() !== '' ? meldungAm : null
    });
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-line bg-cream-warm p-2 text-xs">
      <label className="flex flex-col gap-1 text-ink-soft">
        Weg
        <select
          value={gewaehlt ?? ''}
          onChange={(e) => setWegId(Number(e.target.value))}
          className="min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink"
        >
          {wege.map((w) => (
            <option key={w.id} value={w.id}>
              {w.label}
            </option>
          ))}
        </select>
      </label>

      {istVerlag && (
        <label className="flex flex-col gap-1 text-ink-soft">
          Verlag
          <input
            type="text"
            value={verlagText}
            maxLength={160}
            placeholder="Bei welchem Verlag?"
            onChange={(e) => setVerlagText(e.target.value)}
            className="min-h-[44px] w-44 rounded border border-line bg-cream px-2 text-sm text-ink"
          />
        </label>
      )}

      {istVerlag && (
        <label className="flex flex-col gap-1 text-ink-soft">
          Kanal
          <select
            value={kanalWahl}
            onChange={(e) => setKanalWahl(e.target.value)}
            className="min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink"
          >
            <option value="">— wählen —</option>
            {KANAELE.map(([wert, label]) => (
              <option key={wert} value={wert}>
                {label}
              </option>
            ))}
            <option value={KANAL_SONSTIGES}>Sonstiges</option>
          </select>
        </label>
      )}

      {istVerlag && zeigeFreitext && (
        <label className="flex flex-col gap-1 text-ink-soft">
          Und zwar
          <input
            type="text"
            value={kanalFrei}
            maxLength={40}
            placeholder="Wie bestellt?"
            onChange={(e) => setKanalFrei(e.target.value)}
            className="min-h-[44px] w-40 rounded border border-line bg-cream px-2 text-sm text-ink"
          />
        </label>
      )}

      <label className="flex flex-col gap-1 text-ink-soft">
        Menge
        <input
          type="number"
          // Zahlentastatur auf dem Telefon. `type="number"` allein zeigt
          // dort eine Tastatur mit Sonderzeichen; `inputMode` macht sie
          // zur reinen Zifferntastatur. Menge ist ganzzahlig, also
          // "numeric" und nicht "decimal".
          inputMode="numeric"
          value={menge}
          min={1}
          max={restmenge}
          onChange={(e) => setMenge(Math.max(1, Math.min(restmenge, Number(e.target.value))))}
          className="min-h-[44px] w-20 rounded border border-line bg-cream px-2 text-sm tabular-nums text-ink"
        />
      </label>

      <label className="flex flex-col gap-1 text-ink-soft">
        Bestellt am
        <input
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          className="min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink"
        />
      </label>

      {/* Eigene Zeile (basis-full): auf 390 Pixeln hat neben Weg, Menge
          und Datum kein Textfeld mehr Platz, und geschrumpft wäre es
          unbrauchbar. min-w-0 ist Pflicht — ein <input> schrumpft im
          Flexbereich sonst nicht unter seine Eigenbreite. */}
      <label className="flex min-w-0 basis-full flex-col gap-1 text-ink-soft">
        Meldung des Lieferanten (falls eine kam)
        <input
          type="text"
          value={meldung}
          maxLength={200}
          placeholder="z. B. Nachdruck, Auslieferung KW 40"
          onChange={(e) => setMeldung(e.target.value)}
          className="min-h-[44px] w-full min-w-0 rounded border border-line bg-cream px-2 text-sm text-ink"
        />
      </label>

      {/* Erst wenn etwas gemeldet wurde, gibt es etwas zu datieren. */}
      {meldung.trim() !== '' && (
        <label className="flex flex-col gap-1 text-ink-soft">
          Meldung vom
          <input
            type="date"
            value={meldungAm}
            onChange={(e) => setMeldungAm(e.target.value)}
            className="min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink"
          />
        </label>
      )}

      <button
        type="button"
        disabled={!gueltig}
        onClick={melden}
        className={clsx(
          'min-h-[44px] rounded-md px-3 text-sm',
          gueltig ? 'bg-akzent text-cream hover:bg-akzent-stark' : 'bg-cream-dark text-ink-faint'
        )}
      >
        Zufügen
      </button>
      <button
        type="button"
        onClick={onAbbrechen}
        className="min-h-[44px] rounded-md px-3 text-sm text-ink-soft hover:text-coral"
      >
        Abbrechen
      </button>
    </div>
  );
}

/**
 * Das Eingabefeld für eine Lieferantenmeldung (E11b).
 *
 * Eigene Zeile über die volle Breite und `min-w-0`: dieselbe Falle wie
 * in K11 — ein <input> im Flexbereich schrumpft ohne das nicht unter
 * seine Eigenbreite von rund 180 Pixeln.
 *
 * Leeren und speichern löscht die Meldung. Ein eigener Löschknopf stünde
 * daneben und täte dasselbe; das Feld sagt es klarer.
 */
function MeldungFeld({
  wert,
  onWert,
  datum,
  onDatum,
  onSpeichern,
  onAbbrechen
}: {
  wert: string;
  onWert: (v: string) => void;
  datum: string;
  onDatum: (v: string) => void;
  onSpeichern: () => void;
  onAbbrechen: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-cream-warm p-2">
      <label className="flex min-w-0 basis-full flex-col gap-1 text-xs text-ink-soft">
        Meldung des Lieferanten
        <input
          type="text"
          autoFocus
          value={wert}
          maxLength={200}
          placeholder="Leer lassen entfernt die Meldung"
          onChange={(e) => onWert(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSpeichern();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              onAbbrechen();
            }
          }}
          className="min-h-[44px] w-full min-w-0 rounded border border-line bg-cream px-2 text-sm text-ink"
        />
      </label>
      {/* Nur sinnvoll, solange etwas dasteht: leer speichern löscht die
          Meldung, und dann geht das Datum ohnehin mit. */}
      {wert.trim() !== '' && (
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-soft">
          Meldung vom
          <input
            type="date"
            value={datum}
            onChange={(e) => onDatum(e.target.value)}
            className="min-h-[44px] min-w-0 rounded border border-line bg-cream px-2 text-sm text-ink"
          />
        </label>
      )}
      <button
        type="button"
        onClick={onSpeichern}
        className="inline-flex min-h-[44px] items-center gap-1 rounded-md bg-akzent px-3 text-sm text-cream hover:bg-akzent-stark"
      >
        <Check className="h-4 w-4" />
        Speichern
      </button>
      <button
        type="button"
        onClick={onAbbrechen}
        className="min-h-[44px] rounded-md px-3 text-sm text-ink-soft hover:text-coral"
      >
        Abbrechen
      </button>
    </div>
  );
}

function Sendungsstand({ position: p }: { position: DetailPosition }) {
  const stand = sendungsstandAnzeige(p.sendungsstand, p.menge_wirksam);
  if (stand === null) return null;
  const Symbol = stand.imHaus ? PackageCheck : Truck;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
        stand.imHaus ? 'bg-gold-soft text-gold-dark' : 'bg-teal-soft text-teal'
      )}
      title={
        stand.imHaus
          ? 'Einer Sendung zugeordnet, aber noch im Haus.'
          : 'Diese Position hat das Haus verlassen.'
      }
    >
      <Symbol aria-hidden className="h-3 w-3" />
      {stand.text}
    </span>
  );
}
